/**
 * Value Conversion modeling helpers — the Missing Middle math behind
 * the Value Conversion Analysis page (formerly Gap Analysis).
 *
 * All outputs here are MODELED (clearly labeled in the UI):
 *  - Attribution: per-KPI "where did conversion fail" contributions,
 *    derived from the org's live journey-stage deficits weighted by a
 *    KPI↔stage affinity matrix, plus an External Factors remainder
 *    that grows when the KPI has few data points.
 *  - Confidence: per-projection confidence score from data depth and
 *    recency. High-confidence misses imply navigation failure; low
 *    confidence implies projection risk.
 *  - Waterfall: dollar loss decomposition from potential value (the
 *    largest saved scenario) down to realized value, allocated across
 *    journey stages (85%) + execution variance (15%).
 */

export const STAGE_COLORS = {
  capability: '#6366f1',
  visibility: '#8b5cf6',
  confidence: '#f59e0b',
  coordination: '#10b981',
};

export const STAGE_LABELS = {
  capability: 'Capability Gap',
  visibility: 'Visibility Gap',
  confidence: 'Confidence Gap',
  coordination: 'Coordination Gap',
};

// How strongly each journey stage drives each KPI family. Rows sum to 1.
export const KPI_STAGE_AFFINITY = {
  engagement: { capability: 0.10, visibility: 0.15, confidence: 0.45, coordination: 0.30 },
  csat: { capability: 0.15, visibility: 0.15, confidence: 0.30, coordination: 0.40 },
  productivity: { capability: 0.35, visibility: 0.10, confidence: 0.20, coordination: 0.35 },
  quality: { capability: 0.40, visibility: 0.20, confidence: 0.15, coordination: 0.25 },
  safety: { capability: 0.30, visibility: 0.15, confidence: 0.15, coordination: 0.40 },
  cost_efficiency: { capability: 0.30, visibility: 0.25, confidence: 0.15, coordination: 0.30 },
  speed_market: { capability: 0.25, visibility: 0.15, confidence: 0.20, coordination: 0.40 },
  default: { capability: 0.25, visibility: 0.20, confidence: 0.30, coordination: 0.25 },
};

const STAGE_KEYS = ['capability', 'visibility', 'confidence', 'coordination'];

/**
 * Per-KPI Missing Middle attribution. Returns null when journey-stage
 * context is unavailable.
 *   { contributors: [{key,label,pct}…sorted desc], external: number }
 */
export function computeAttribution(kpi, stages) {
  if (!stages) return null;
  const aff = KPI_STAGE_AFFINITY[kpi.kpi_id] || KPI_STAGE_AFFINITY.default;
  const weighted = {};
  let sum = 0;
  STAGE_KEYS.forEach((k) => {
    const deficit = Math.max(0.02, 1 - (stages[k] ?? 0));
    weighted[k] = (aff[k] || 0.25) * deficit;
    sum += weighted[k];
  });
  if (!sum) return null;
  const dataPoints = kpi.actual_data_points || 0;
  const external = Math.min(26, Math.max(10, 10 + 2 * Math.max(0, 8 - dataPoints)));
  const scale = (100 - external) / sum;
  const rows = STAGE_KEYS.map((k) => ({
    key: k,
    label: STAGE_LABELS[k],
    pct: Math.round(weighted[k] * scale),
  }));
  const drift = (100 - external) - rows.reduce((a, r) => a + r.pct, 0);
  rows[0].pct += drift;
  rows.sort((a, b) => b.pct - a.pct);
  return { contributors: rows, external };
}

/**
 * Modeled projection confidence (0–95) + off-course classification:
 *   'navigation_failure'  → high-confidence projection missed: the
 *                           journey failed, not the forecast
 *   'projection_risk'     → low-signal projection: validate baseline
 */
export function computeConfidence(kpi) {
  const pts = kpi.actual_data_points || 0;
  let conf = Math.min(95, 40 + pts * 4.5);
  let stale = false;
  if (kpi.latest_actual_date) {
    const ageDays = (Date.now() - new Date(kpi.latest_actual_date).getTime()) / 864e5;
    if (ageDays > 120) { conf -= 8; stale = true; }
  }
  conf = Math.max(35, Math.round(conf));
  let classification = null;
  if ((kpi.gap ?? 0) < 0 && kpi.has_projection && kpi.has_actual) {
    classification = conf >= 70 ? 'navigation_failure' : 'projection_risk';
  }
  return { confidence: conf, stale, classification };
}

/**
 * Missing Middle loss waterfall. Returns null when there's no dollar
 * anchor or no comparable KPI pairs.
 *   { potential, actual, totalLoss, realizationPct, steps: [
 *       {label, amount, kind: 'total'|'loss'|'gain'} ] }
 */
export function computeWaterfall(gapRows, stages, potential) {
  if (!potential?.amount || !gapRows?.length) return null;
  const pairs = gapRows.filter(
    (g) => g.has_projection && g.has_actual && (g.projected_improvement || 0) > 0,
  );
  if (!pairs.length) return null;

  const ratios = pairs.map((g) =>
    Math.min(1, Math.max(0, g.actual_improvement / g.projected_improvement)),
  );
  const realization = ratios.reduce((a, b) => a + b, 0) / ratios.length;
  const actual = potential.amount * realization;
  const totalLoss = potential.amount - actual;

  const steps = [{ label: 'Potential Value', amount: potential.amount, kind: 'total' }];

  if (totalLoss > 0) {
    const executionShare = 0.15;
    let stageLosses = [];
    if (stages) {
      const deficits = STAGE_KEYS.map((k) => ({ key: k, d: Math.max(0.02, 1 - (stages[k] ?? 0)) }));
      const dSum = deficits.reduce((a, r) => a + r.d, 0);
      stageLosses = deficits.map((r) => ({
        label: `${r.key.charAt(0).toUpperCase()}${r.key.slice(1)} Loss`,
        key: r.key,
        amount: totalLoss * (1 - executionShare) * (r.d / dSum),
        kind: 'loss',
      }));
    } else {
      stageLosses = [{ label: 'Conversion Shortfall', amount: totalLoss * (1 - executionShare), kind: 'loss' }];
    }
    steps.push(...stageLosses);
    steps.push({ label: 'Execution Variance', amount: totalLoss * executionShare, kind: 'loss' });
  }

  steps.push({ label: 'Actual Value', amount: actual, kind: 'total' });

  return {
    potential: potential.amount,
    actual,
    totalLoss,
    realizationPct: Math.round(realization * 100),
    steps,
  };
}

/** $11.4M / $850k / $920 compact money formatter. */
export function fmtMoney(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 1 : 2).replace(/\.0+$/, '')}M`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}k`;
  return `${sign}$${Math.round(abs)}`;
}
